import styled from 'styled-components'
import { useIntl } from 'react-intl'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'

interface Props {
  columns: TableColumn[]
  rowCount: number
  hasMoreRows: boolean
  loadMore: () => void
  sortDirection?: 'asc' | 'desc'
  sortByKey?: string
  sortHandler?: (key: string) => void
  noMoreRowsText?: string
  children: JSX.Element[]
  status?: RequestStatus
}

export default function InfiniteScrollTable({
  columns,
  rowCount,
  hasMoreRows,
  loadMore,
  sortDirection = 'desc',
  sortByKey,
  sortHandler,
  noMoreRowsText,
  children,
  status,
}: Props): JSX.Element {
  const intl = useIntl()
  const hasWidthSettings = () => {
    return columns.length > 0 ? columns[0].width !== undefined : false
  }

  return (
    <TableContainer sx={{ borderRadius: '10px' }}>
      {rowCount === 0 && status === 'succeeded' && (
        <NoResults>{intl.formatMessage({ id: 'strings.noResults' })}</NoResults>
      )}
      {rowCount > 0 && (
        <>
          <StyledTable aria-labelledby="tableTitle" size="small" id="infinite-table">
            {hasWidthSettings() && (
              <colgroup>
                {columns.map((column: TableColumn) => (
                  <col key={`tablecol-${column.id}`} style={{ width: `${column.width}%` }} />
                ))}
              </colgroup>
            )}
            <StyledTableHead>
              <TableRow>
                {columns.map((column: TableColumn) => (
                  <TableCell
                    key={`tablecell-${column.id}`}
                    align={column.numeric ? 'right' : 'left'}
                    sortDirection={sortByKey === column.id ? sortDirection : false}
                  >
                    {column.sortable && sortHandler ? (
                      <TableSortLabel
                        active={sortByKey === column.id}
                        direction={sortByKey === column.id ? sortDirection : 'asc'}
                        onClick={() => sortHandler(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>{children}</TableBody>
          </StyledTable>
          <InfiniteScroll
            dataLength={rowCount}
            next={() => loadMore()}
            hasMore={hasMoreRows}
            loader={
              <ContentContainer>
                <LoadingSpinnerGreen size={36} />
              </ContentContainer>
            }
            endMessage={<ContentContainer>{noMoreRowsText}</ContentContainer>}
            scrollableTarget={<StyledTable />}
            hasChildren={false}
            children={[]}
          />
        </>
      )}
    </TableContainer>
  )
}

const StyledTableHead = styled(TableHead)`
  background-color: ${(props) => props.theme.palette.primary.dark};
  height: 40px;

  & th,
  span,
  span.MuiTableSortLabel-active {
    color: ${(props) => props.theme.customPalette.light};
    font-size: 13px;
    line-height: 15px;
    text-transform: uppercase;
  }
  & span:hover {
    color: ${(props) => props.theme.customPalette.light};
    opacity: 0.8;
  }
  & svg {
    color: #fff !important;
  }
  border-radius: 10px;
`

const StyledTable = styled(Table)``

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 30px auto;
  justify-content: center;
`

const NoResults = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`
